import { ModificationCitation, PresentationModification, ResponseMode } from "../../types";
import {
  attachModelDiagnostic,
  inferParagraphSemanticsForCreateShapes,
  normalizeParagraphOpsInModifications,
  normalizeInSlideChartsInModifications,
  normalizeTableOpsInModifications,
} from "../../utils";

export interface FinalizeModelModificationsOptions {
  inferCreateShapeParagraphSemantics?: boolean;
}

const CHAT_REPLY_FALLBACK = "Sorry, I don't have a response to that.";

type SlideWithMisplacedCitations = {
  citations?: unknown;
};

function isValidModificationCitation(value: unknown): value is ModificationCitation {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<Record<keyof ModificationCitation, unknown>>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.dataSource === "string" &&
    typeof candidate.type === "string" &&
    typeof candidate.value === "string"
  );
}

function normalizeMisplacedSlideCitations(modifications: PresentationModification): void {
  const existingCitations = Array.isArray(modifications.citations)
    ? modifications.citations.filter(isValidModificationCitation)
    : [];
  const seen = new Set(
    existingCitations.map(
      (citation) =>
        `${citation.id}\u0000${citation.dataSource}\u0000${citation.type}\u0000${citation.value}`
    )
  );
  const liftedCitations: ModificationCitation[] = [];
  let slideFieldsRemoved = 0;

  for (const slide of modifications.slides) {
    const malformedSlide = slide as SlideWithMisplacedCitations;
    if (malformedSlide.citations === undefined) {
      continue;
    }

    slideFieldsRemoved += 1;
    const slideCitations = Array.isArray(malformedSlide.citations)
      ? malformedSlide.citations
      : [];

    for (const citation of slideCitations) {
      if (!isValidModificationCitation(citation)) {
        continue;
      }
      const key = `${citation.id}\u0000${citation.dataSource}\u0000${citation.type}\u0000${citation.value}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      liftedCitations.push(citation);
    }

    delete malformedSlide.citations;
  }

  if (slideFieldsRemoved === 0) {
    return;
  }

  modifications.citations = [...existingCitations, ...liftedCitations];
  attachModelDiagnostic(
    modifications,
    "MODEL_OUTPUT_SLIDE_LEVEL_CITATIONS",
    `Model response put citations on ${slideFieldsRemoved} slide object(s); lifted ${liftedCitations.length} citation(s) to top-level citations[].`
  );
}

function normalizeModeAndReply(modifications: PresentationModification): void {
  const rawMode = (modifications.mode as ResponseMode | undefined) ?? undefined;
  const slidesCount = Array.isArray(modifications.slides) ? modifications.slides.length : 0;
  const trimmedReply =
    typeof modifications.reply === "string" ? modifications.reply.trim() : "";
  const trimmedSummary =
    typeof modifications.summary === "string" ? modifications.summary.trim() : "";

  if (rawMode !== "chat" && rawMode !== "edit") {
    const inferred: ResponseMode =
      slidesCount > 0 ? "edit" : trimmedReply.length > 0 ? "chat" : "chat";
    modifications.mode = inferred;
    attachModelDiagnostic(
      modifications,
      "MODEL_OUTPUT_MISSING_MODE",
      `Model response omitted the required "mode" field; inferred "${inferred}" from payload.`
    );
  }

  if (modifications.mode === "chat") {
    if (slidesCount > 0) {
      modifications.slides = [];
      attachModelDiagnostic(
        modifications,
        "MODEL_OUTPUT_CHAT_WITH_SLIDES",
        `Chat-mode response included ${slidesCount} slide modification(s); dropped them.`
      );
    }
    if (trimmedSummary.length > 0 && trimmedReply.length === 0) {
      modifications.reply = trimmedSummary;
      modifications.summary = undefined;
      attachModelDiagnostic(
        modifications,
        "MODEL_OUTPUT_CHAT_USED_SUMMARY",
        "Chat-mode response set `summary` instead of `reply`; promoted summary to reply."
      );
    } else if (trimmedReply.length === 0) {
      modifications.reply = CHAT_REPLY_FALLBACK;
      attachModelDiagnostic(
        modifications,
        "MODEL_OUTPUT_MISSING_REPLY",
        "Chat-mode response omitted `reply`; substituted a friendly fallback."
      );
    } else {
      modifications.reply = trimmedReply;
    }
    if (modifications.summary !== undefined && modifications.reply !== modifications.summary) {
      modifications.summary = undefined;
    }
  } else {
    if (slidesCount === 0) {
      modifications.mode = "chat";
      if (trimmedReply.length === 0) {
        modifications.reply =
          trimmedSummary.length > 0 ? trimmedSummary : CHAT_REPLY_FALLBACK;
      } else {
        modifications.reply = trimmedReply;
      }
      modifications.summary = undefined;
      attachModelDiagnostic(
        modifications,
        "MODEL_OUTPUT_EDIT_NO_SLIDES",
        "Edit-mode response contained no slide modifications; downgraded to chat mode."
      );
    } else if (trimmedReply.length > 0) {
      modifications.reply = undefined;
      attachModelDiagnostic(
        modifications,
        "MODEL_OUTPUT_EDIT_WITH_REPLY",
        "Edit-mode response included `reply`; dropped (use `summary` for edit-mode prose)."
      );
    }
  }
}

export function finalizeModelModifications(
  modifications: PresentationModification,
  options: FinalizeModelModificationsOptions = {}
): PresentationModification {
  const hasSlidesArray = Array.isArray(modifications.slides);

  if (!hasSlidesArray) {
    modifications.slides = [];
    attachModelDiagnostic(
      modifications,
      "MODEL_OUTPUT_MISSING_SLIDES",
      "Model response omitted the required slides array."
    );
  }

  normalizeMisplacedSlideCitations(modifications);

  normalizeModeAndReply(modifications);

  if (options.inferCreateShapeParagraphSemantics) {
    inferParagraphSemanticsForCreateShapes(modifications);
  }

  const paragraphNormalizationStats = normalizeParagraphOpsInModifications(modifications);
  if (paragraphNormalizationStats.droppedOps > 0) {
    attachModelDiagnostic(
      modifications,
      "MODEL_OUTPUT_INVALID_PARAGRAPH_OP",
      `Dropped ${paragraphNormalizationStats.droppedOps} invalid paragraph operation(s) from model output.`
    );
  }

  const chartNormalizationStats = normalizeInSlideChartsInModifications(modifications);
  for (const issue of chartNormalizationStats.issues) {
    attachModelDiagnostic(modifications, issue.code, issue.message);
  }

  const tableNormalizationStats = normalizeTableOpsInModifications(modifications);
  for (const issue of tableNormalizationStats.issues) {
    attachModelDiagnostic(modifications, issue.code, issue.message);
  }

  return modifications;
}
